import React, { useState } from "react";
import { Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "../components/Wordmark";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_tgi-golfers/artifacts/h0duc0uc_TGI%20Logo%20international.png";

const palette = [
  { name: "Brand Black", hex: "#0A0A0A", rgb: "10, 10, 10", role: "Primary background, logo plate, dark sections", text: "text-background", bg: "bg-foreground" },
  { name: "Fairway Green", hex: "#3B6F3C", rgb: "59, 111, 60", role: "Primary brand colour, accents, CTAs", text: "text-primary-foreground", bg: "bg-primary" },
  { name: "Sand", hex: "#CBA976", rgb: "203, 169, 118", role: "Secondary accent, sponsor highlights", text: "text-foreground", bg: "bg-secondary" },
  { name: "Off-White", hex: "#FAFAFA", rgb: "250, 250, 250", role: "Page background, primary canvas", text: "text-foreground", bg: "bg-background" },
  { name: "Charcoal", hex: "#1A1A1A", rgb: "26, 26, 26", role: "Body text", text: "text-background", bg: "bg-foreground" },
  { name: "Muted Stone", hex: "#F1F3F5", rgb: "241, 243, 245", role: "Soft background blocks", text: "text-foreground", bg: "bg-muted" },
];

const typography = [
  { font: "Manrope", role: "Page headlines (H1, H2)", style: "Geometric sans, weight 800, tight tracking", sample: "Every golfer has a story" },
  { font: "Anton", role: "Brand wordmark, stat figures", style: "Condensed bold sans-serif, uppercase only", sample: "TALK GOLF INTERNATIONAL" },
  { font: "Manrope", role: "Body copy, UI labels, overlines", style: "Geometric sans, weights 300, 400, 500, 600, 700", sample: "Supporting the next generation of golf talent." },
];

export default function BrandAssets() {
  const [copied, setCopied] = useState("");

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    setCopied(val);
    toast.success(`Copied ${val}`);
    setTimeout(() => setCopied(""), 1500);
  };

  const download = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div data-testid="brand-assets-page">
      {/* HERO */}
      <section className="border-b border-border">
        <div className="container-tight py-20 md:py-28 max-w-4xl">
          <div className="overline">Brand & press kit</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-tight mt-3 leading-[0.95]">
            The <span className="text-primary">TGI Golfer Fund</span> brand kit.
          </h1>
          <p className="mt-8 max-w-2xl text-foreground/70 leading-relaxed">
            Everything you need to talk about, partner with, or write about the Talk Golf International Golfer Fund.
            All assets here are licensed for press and sponsor use. Please respect the clear space and colour
            guidance below.
          </p>
        </div>
      </section>

      {/* LOGO DOWNLOADS */}
      <section className="border-b border-border">
        <div className="container-tight py-20">
          <div className="overline mb-3">01 · Logo</div>
          <h2 className="font-display text-4xl md:text-5xl max-w-2xl">The wordmark in two presentations.</h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
            {/* Dark presentation */}
            <div className="bg-foreground p-12 flex flex-col items-center justify-between gap-10 min-h-[360px]">
              <div className="overline text-background/60">On dark</div>
              <div data-testid="logo-dark-preview" className="scale-150">
                <Wordmark size="lg" dark withFund={false} />
              </div>
              <button
                onClick={() => download(LOGO_URL, "tgi-golfer-fund-logo-source.png")}
                className="btn-sharp bg-background text-foreground hover:bg-secondary hover:text-foreground inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]"
                data-testid="download-logo-dark"
              >
                <Download size={14} /> Download source PNG
              </button>
            </div>

            {/* Light presentation */}
            <div className="bg-background p-12 flex flex-col items-center justify-between gap-10 min-h-[360px] border-t md:border-t-0 md:border-l border-border">
              <div className="overline">On light</div>
              <div className="scale-150">
                <Wordmark size="lg" withFund={false} />
              </div>
              <button
                onClick={() => download(LOGO_URL, "tgi-golfer-fund-logo-source.png")}
                className="btn-primary inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]"
                data-testid="download-logo-light"
              >
                <Download size={14} /> Download source PNG
              </button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-foreground/70">
            <div>
              <div className="overline mb-2">Clear space</div>
              <p>Maintain at least the height of the "G" in GOLF around all sides of the wordmark.</p>
            </div>
            <div>
              <div className="overline mb-2">Minimum size</div>
              <p>Do not use the wordmark smaller than 80px wide on screen, or 20mm in print.</p>
            </div>
            <div>
              <div className="overline mb-2">Don't</div>
              <p>Don't recolour, distort, rotate, or place the wordmark on busy imagery without a plate.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COLOUR PALETTE */}
      <section className="border-b border-border bg-muted">
        <div className="container-tight py-20">
          <div className="overline mb-3">02 · Colour palette</div>
          <h2 className="font-display text-4xl md:text-5xl max-w-2xl">A grounded, golf-led palette.</h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-border bg-background">
            {palette.map((c, i) => (
              <div key={c.name} className={`border-border ${i % 3 !== 2 ? "lg:border-r" : ""} ${i < palette.length - 3 ? "lg:border-b" : ""} ${i < palette.length - 1 ? "border-b lg:border-b" : ""}`}
                   data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                <div className={`${c.bg} ${c.text} aspect-[16/9] flex items-end p-5`}>
                  <div className="font-display text-3xl">{c.name}</div>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <button
                      onClick={() => copy(c.hex)}
                      className="flex items-center justify-between gap-2 hover:text-primary transition-colors text-left"
                      data-testid={`copy-hex-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <span>
                        <span className="overline block text-foreground/50">Hex</span>
                        <span className="font-medium">{c.hex}</span>
                      </span>
                      {copied === c.hex ? <Check size={14} className="text-primary" /> : <Copy size={14} className="text-foreground/40" />}
                    </button>
                    <button
                      onClick={() => copy(c.rgb)}
                      className="flex items-center justify-between gap-2 hover:text-primary transition-colors text-left"
                    >
                      <span>
                        <span className="overline block text-foreground/50">RGB</span>
                        <span className="font-medium">{c.rgb}</span>
                      </span>
                      {copied === c.rgb ? <Check size={14} className="text-primary" /> : <Copy size={14} className="text-foreground/40" />}
                    </button>
                  </div>
                  <p className="mt-4 text-xs text-foreground/60 leading-relaxed">{c.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TYPOGRAPHY */}
      <section className="border-b border-border">
        <div className="container-tight py-20">
          <div className="overline mb-3">03 · Typography</div>
          <h2 className="font-display text-4xl md:text-5xl max-w-2xl">Three voices, one type system.</h2>

          <div className="mt-12 divide-y divide-border border-y border-border">
            {typography.map((t) => (
              <div key={t.font} className="py-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-start" data-testid={`type-${t.font.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="md:col-span-3">
                  <div className="overline">{t.role}</div>
                  <div className="font-display text-2xl mt-2">{t.font}</div>
                  <div className="text-xs text-foreground/60 mt-2">{t.style}</div>
                </div>
                <div className="md:col-span-9">
                  <div
                    className="text-5xl md:text-7xl leading-[1.05]"
                    style={{
                      fontFamily: t.font === "Anton" ? "Anton, sans-serif" : "Manrope, sans-serif",
                      textTransform: t.font === "Anton" ? "uppercase" : "none",
                      fontWeight: t.font === "Anton" ? 400 : (t.role.includes("headlines") ? 800 : 500),
                      letterSpacing: t.role.includes("headlines") ? "-0.035em" : "normal",
                    }}
                  >
                    {t.sample}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-sm text-foreground/70 max-w-2xl">
            All three families are free, open-source webfonts available on Google Fonts. We recommend self-hosting for
            print production.
          </div>
        </div>
      </section>

      {/* VOICE & TONE */}
      <section className="bg-foreground text-background">
        <div className="container-tight py-20">
          <div className="overline text-background/60 mb-3">04 · Voice & tone</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">
            Direct, golf-led, <span className="text-secondary">golfer-first</span>.
          </h2>
          <p className="mt-6 max-w-2xl text-background/70 leading-relaxed">
            We speak the language of the game. Facts over hype. Golfers over taglines. Real tournaments, real
            handicaps, real journeys. If a sentence wouldn't survive a clubhouse conversation, it doesn't make the page.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <div className="overline text-background/60 mb-3">Yes</div>
              <ul className="space-y-3 text-sm text-background/80">
                <li>· Golfer-first. Their name on the page, ours in the background.</li>
                <li>· Specific results. Real tournaments. Real numbers. Real handicaps.</li>
                <li>· Plain English. The way golfers actually talk.</li>
                <li>· Quiet confidence. We trust the work to speak for itself.</li>
              </ul>
            </div>
            <div>
              <div className="overline text-background/60 mb-3">No</div>
              <ul className="space-y-3 text-sm text-background/80">
                <li>· Marketing speak, slogans, fundraising language.</li>
                <li>· Hype, hashtags, exclamation marks.</li>
                <li>· Claims without proof, vague achievements.</li>
                <li>· Anything that puts the platform in front of the player.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="border-t border-border">
        <div className="container-tight py-16 text-center max-w-2xl">
          <div className="overline">Press enquiries</div>
          <h2 className="font-display text-3xl md:text-5xl mt-3">Need something not listed here?</h2>
          <p className="mt-6 text-foreground/70">
            Email <a href="mailto:golfersfund@talkgolfireland.com" className="text-primary underline">golfersfund@talkgolfireland.com</a> for
            high-resolution assets, golfer portraits, founder bios, and interview requests.
          </p>
        </div>
      </section>
    </div>
  );
}
